<template>
  <Layout title="Assignments">
    <div class="report-content">
      <div class="report-heading">
        <div>
          <h1>Assignments</h1>
          <p>Track assignment activity, weight and volume.</p>
        </div>
      </div>
      <div class="report-filters">
        <DateRangePicker id="date-range" :value="dateRangeValue" @update:value="handleDateChanged" />
      </div>
      <b-row>
        <b-col>
          <Stats :stats="stats" />
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <Sendings :assignments="assignments" />
        </b-col>
      </b-row>
    </div>
  </Layout>
</template>
<script lang="ts">
import type { DataCollection } from '@/typings/DataCollection'
import type { Periods } from '@/typings/Periods'
import type { PeriodsDiff } from '@/typings/Shipments/PeriodsDiff'
import type { ComponentData } from '@/typings/Assignments/ComponentData'
import type { StatisticItem } from '@/typings/Assignments/StatisticItem'
import { defineComponent, ref, reactive, onMounted, watch, type Ref } from 'vue'
import Layout from '@/views/dashboard/analytics-shell.vue'
import DateRangePicker from '@/views/dashboard/partials/report-period.vue'
import Sendings from '@/views/dashboard/partials/sendings.vue'
import Stats from '@/views/dashboard/partials/stats.vue'
import Assignments from '@/requests/Assignments'
import ErrorHandler from '@/requests/ErrorHandler'
import moment from 'moment'

export default defineComponent<ComponentData>({
  components: { DateRangePicker, Stats, Sendings, Layout },
  setup() {
    const assignmentsRequest = Assignments()

    const dateRangeValue: Ref<string> = ref(
      moment().subtract(29, 'days').format('YYYY-MM-DD') + ' - ' + moment().format('YYYY-MM-DD'),
    )
    const filters = reactive({
      period: dateRangeValue.value,
      distributionChannel: null,
      customerId: null,
      serviceLevel: null,
    })

    const stats: Ref<StatisticItem[]> = ref([
      { icon: 'mdi-book-multiple-outline', number: '', title: 'Batch assignments', color: 'success', subtext: '' },
      { icon: 'mdi-book-outline', number: '', title: 'Single assignment', color: 'danger', subtext: '' },
      { icon: 'mdi-weight-kilogram', number: '', title: 'Gross weight', color: 'danger', subtext: '' },
      { icon: 'mdi-bottle-tonic-outline', number: '', title: 'Volume', color: 'success', subtext: '' },
    ])

    const assignments: Ref<Periods> = ref({
      currentPeriod: [],
      previousPeriod: [],
      periodLabels: [],
    })

    const handleDateChanged = (value: string) => {
      filters.period = value
      dateRangeValue.value = value
    }

    const getAssignments = () => {
      assignmentsRequest
        .getPeriod(filters)
        .then((data: Periods) => {
          assignments.value = data
        })
        .catch(ErrorHandler.handle)
    }

    const getStatsData = () => {
      assignmentsRequest
        .getTotals(filters)
        .then((data: DataCollection<PeriodsDiff>) => {
          const defaultData = {
            currentPeriod: '0',
            previousPeriod: '0',
            diff: '0',
          }
          let batchCount = defaultData,
            singleCount = defaultData,
            grossWeight = defaultData,
            volume = defaultData

          data.data.forEach((item: PeriodsDiff) => {
            switch (item.name) {
              case 'batchCount':
                batchCount = item
                break
              case 'singleCount':
                singleCount = item
                break
              case 'grossWeight':
                grossWeight = item
                break
              case 'volume':
                volume = item
                break
            }
          })

          stats.value = [
            {
              icon: 'mdi-book-multiple-outline',
              number: String(batchCount.currentPeriod),
              title: 'Batch assignments',
              color: parseFloat(batchCount.diff) >= 0 ? 'success' : 'danger',
              subtext: `${batchCount.diff}%`,
            },
            {
              icon: 'mdi-book-outline',
              number: String(singleCount.currentPeriod),
              title: 'Single assignment',
              color: parseFloat(singleCount.diff) >= 0 ? 'success' : 'danger',
              subtext: `${singleCount.diff}%`,
            },
            {
              icon: 'mdi-weight-kilogram',
              number: `${(parseInt(grossWeight.currentPeriod) / 1000).toFixed(2)}kg`,
              title: 'Gross weight',
              color: parseFloat(grossWeight.diff) >= 0 ? 'success' : 'danger',
              subtext: `${grossWeight.diff}%`,
            },
            {
              icon: 'mdi-bottle-tonic-outline',
              number: `${(parseInt(volume.currentPeriod) / 1000).toFixed(2)}L`,
              title: 'Volume',
              color: parseFloat(volume.diff) >= 0 ? 'success' : 'danger',
              subtext: `${volume.diff}%`,
            },
          ]
        })
        .catch(ErrorHandler.handle)
    }

    watch(
      () => filters.period,
      () => {
        getStatsData()
        getAssignments()
      },
      { deep: true },
    )

    onMounted(() => {
      getStatsData()
      getAssignments()
    })

    return {
      dateRangeValue,
      stats,
      assignments,
      handleDateChanged,
    }
  },
})
</script>
